import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiShoppingCart, FiArrowLeft } from "react-icons/fi";
import Navbar from "../../components/Navbar/Navbar";
import { getProductById } from "../AdminDashboard/Content/services/menuItems.service";
import { useCart } from "../../context/CartContext";

const StarIcon = ({ className = "w-5 h-5 text-[#000000]" }) => (
  <svg className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
    <path d="M13.849 4.22c-.684-1.626-3.014-1.626-3.698 0L8.397 8.387l-4.552.361c-1.775.14-2.495 2.331-1.142 3.477l3.468 2.937-1.06 4.392c-.413 1.713 1.472 3.067 2.992 2.149L12 19.35l3.897 2.354c1.52.918 3.405-.436 2.992-2.15l-1.06-4.39 3.468-2.938c1.353-1.146.633-3.336-1.142-3.477l-4.552-.36-1.754-4.17Z" />
  </svg>
);

const ProductDetails = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const data = await getProductById(id);
        if (!cancelled) setProduct(data);
      } catch (err) {
        console.error("Failed to load product", err);
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const showToast = (item) => {
    setToast(item);
    setTimeout(() => setToast(null), 4000);
  };

  const handleAddToCart = () => {
    if (!product || product.isActive === false) return;
    const item = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      imageUrl: product.imageUrl,
    };
    addItem(item);
    showToast(item);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] font-playfair">
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-24 flex flex-col items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#e0d8c3] border-t-transparent" />
          <p className="mt-4 text-[#a6a6a6]">Duke ngarkuar...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#000000] font-playfair">
        <Navbar />
        <div className="mx-auto max-w-6xl px-4 py-24 text-center">
          <p className="text-xl text-[#e0d8c3]">Produkti nuk u gjet.</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 text-[#e0d8c3] hover:underline"
          >
            <FiArrowLeft /> Kthehu në faqen kryesore
          </Link>
        </div>
      </div>
    );
  }

  const isAvailable = product.isActive !== false;
  const description = product.shenime || product.description || "";

  return (
    <div className="min-h-screen bg-[#000000] font-playfair">
      <Navbar />

      <div className="mx-auto max-w-6xl px-4 py-8 sm:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#e0d8c3] hover:underline mb-6 sm:mb-8"
        >
          <FiArrowLeft /> Kthehu te koleksioni
        </Link>

        <div className="rounded-2xl border border-[#a6a6a6]/30 bg-white overflow-hidden shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative flex min-h-[280px] md:min-h-[400px] w-full items-center justify-center overflow-hidden bg-[#a6a6a6]/10">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={product.name}
                  className={`h-full w-full object-contain transition-all ${!isAvailable ? "grayscale opacity-70" : ""}`}
                />
              ) : (
                <span className="text-[#a6a6a6]">Pa foto</span>
              )}
              {!isAvailable && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#000000]/50">
                  <span className="rounded border border-[#a6a6a6] bg-[#000000]/90 px-4 py-2 text-sm font-bold uppercase text-[#e0d8c3]">
                    Mungon në stok
                  </span>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col p-6 sm:p-8 md:p-10">
              {/* Stars + badge */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <StarIcon key={i} />
                  ))}
                </div>
                <span className="rounded border border-[#a6a6a6]/40 bg-[#e0d8c3]/30 px-1.5 py-0.5 text-xs font-medium text-[#000000]">
                  Cilësi
                </span>
              </div>

              <h1 className="text-2xl font-bold tracking-tight text-[#000000] sm:text-3xl">
                {product.name}
              </h1>

              <p className="mt-6 text-3xl font-extrabold text-[#000000]">
                €{Number(product.price).toFixed(2)}
              </p>

              {description && (
                <div className="mt-6">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-[#a6a6a6] mb-2">
                    Përshkrim
                  </h2>
                  <p className="text-[#000000]/90 leading-relaxed whitespace-pre-wrap">
                    {description}
                  </p>
                </div>
              )}

              <div className="mt-auto pt-8">
                {isAvailable ? (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className="inline-flex items-center gap-2 w-full sm:w-auto justify-center rounded-xl border border-transparent bg-[#000000] px-6 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-[#000000]/90 focus:ring-4 focus:ring-[#a6a6a6]/50 focus:outline-none transition"
                  >
                    <FiShoppingCart className="w-5 h-5" />
                    Shto në shportë
                  </button>
                ) : (
                  <div className="inline-flex items-center justify-center rounded-xl border-2 border-dashed border-[#a6a6a6] bg-[#a6a6a6]/10 px-6 py-3.5 text-base font-medium text-[#a6a6a6]">
                    Mungon në stok
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 animate-slide-in"
          role="status"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-[#a6a6a6]/40 bg-white p-4 shadow-2xl">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-[#e0d8c3]/30">
              {toast.imageUrl ? (
                <img src={toast.imageUrl} alt={toast.name} className="h-full w-full object-contain" />
              ) : null}
            </div>
            <div>
              <p className="font-semibold text-[#000000]">Shtuar në shportë</p>
              <p className="text-sm text-[#a6a6a6]">{toast.name}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
